import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { BaseDialog } from "@/lib/Components/Dialog/BaseDialog";
import {
  SupplierNotificationSettingEvent,
  SupplierNotificationSettingType,
} from "@/gql/graphql";
import {
  getQueryKey,
  useGqlMutation,
  useOrgId,
} from "@/lib/GraphQLCodegen/fetcher";
import { updateSupplierMutation } from "@/app/Suppliers/GraphQL/supplierMutations";
import {
  supplierRecordQuery,
  type SupplierRecord,
} from "@/app/Suppliers/GraphQL/supplierRecordQuery";
import { useSupplier } from "@/app/Suppliers/Hooks/useSupplierRecord";
import {
  EventDef,
  EventSection,
  useSupplierNotificationEvents,
} from "@/app/Suppliers/Hooks/useSupplierNotificationEvents";
import { Field, Label } from "@/components/catalyst/fieldset";
import * as Headless from "@headlessui/react";
import { Input } from "@/components/catalyst/input";
import { Checkbox } from "@/components/catalyst/checkbox";
import { Select } from "@/components/catalyst/select";

type SupplierNotificationEmailDialogProps = {
  supplierId: string;
  email?: string;
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type NotificationSetting = NonNullable<
  NonNullable<SupplierRecord>["notificationSettings"]
>[number];

type CacheShape = { supplier: NonNullable<SupplierRecord> };

export function SupplierNotificationEmailDialog({
  supplierId,
  email,
}: SupplierNotificationEmailDialogProps) {
  const supplier = useSupplier(supplierId);
  const sections = useSupplierNotificationEvents();
  const queryClient = useQueryClient();
  const orgId = useOrgId();
  const supplierQueryKey = getQueryKey(
    supplierRecordQuery,
    { id: supplierId },
    orgId,
  );

  const { mutateAsync } = useGqlMutation(updateSupplierMutation, {
    onMutate: async (variables: any) => {
      const upsert: NotificationSetting[] =
        variables.input?.notificationSettings?.upsert ?? [];
      const deleteIds: string[] =
        variables.input?.notificationSettings?.delete ?? [];

      await queryClient.cancelQueries({ queryKey: supplierQueryKey });
      const previous = queryClient.getQueryData<CacheShape>(supplierQueryKey);

      queryClient.setQueryData<CacheShape>(supplierQueryKey, (old) => {
        if (!old?.supplier) return old;
        const current = old.supplier.notificationSettings ?? [];

        const afterDelete = current.filter((s) => !deleteIds.includes(s.id));

        const next = [...afterDelete];
        for (const item of upsert) {
          if (item.id) {
            const idx = next.findIndex((s) => s.id === item.id);
            if (idx >= 0) {
              next[idx] = { ...next[idx], ...item };
            }
          } else {
            next.push({
              __typename: "SupplierNotificationSetting",
              id: `optimistic-${crypto.randomUUID()}`,
              email: item.email,
              event: item.event,
              type: item.type,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
            } as NotificationSetting);
          }
        }

        return {
          ...old,
          supplier: { ...old.supplier, notificationSettings: next },
        };
      });

      return { previous };
    },
    onError: (_err, _vars, context: any) => {
      if (context?.previous) {
        queryClient.setQueryData(supplierQueryKey, context.previous);
      }
    },
  });

  const [committedEmail, setCommittedEmail] = useState(email ?? "");
  const [emailDraft, setEmailDraft] = useState(email ?? "");
  const [emailError, setEmailError] = useState<string | null>(null);

  const isAddMode = !email;
  const trimmedDraft = emailDraft.trim();
  const isValidDraft = EMAIL_REGEX.test(trimmedDraft);

  const effectiveEmail = isAddMode
    ? isValidDraft
      ? trimmedDraft
      : ""
    : committedEmail;
  const isValidEmail = EMAIL_REGEX.test(effectiveEmail);
  const existingSettings = isValidEmail
    ? (supplier.notificationSettings ?? []).filter(
        (s) => s.email === effectiveEmail,
      )
    : [];

  async function commit(input: {
    upsert: {
      id?: string;
      email: string;
      event: SupplierNotificationSettingEvent;
      type: SupplierNotificationSettingType;
    }[];
    delete: string[];
  }) {
    await mutateAsync({
      id: supplierId,
      input: { notificationSettings: input },
    });
  }

  async function handleEmailBlur() {
    const trimmed = emailDraft.trim();
    if (!trimmed) {
      setEmailError(null);
      return;
    }
    if (!EMAIL_REGEX.test(trimmed)) {
      setEmailError("Invalid email");
      return;
    }
    setEmailError(null);

    if (trimmed === effectiveEmail) return;

    const duplicate = (supplier.notificationSettings ?? []).some(
      (s) => s.email === trimmed,
    );
    if (duplicate) {
      setEmailError("Already a recipient");
      return;
    }

    if (existingSettings.length > 0) {
      await commit({
        upsert: existingSettings.map((s) => ({
          id: s.id,
          email: trimmed,
          event: s.event,
          type: s.type,
        })),
        delete: [],
      });
    }
    setCommittedEmail(trimmed);
  }

  async function toggleSubscribed(eventDef: EventDef, nextSubscribed: boolean) {
    if (!isValidEmail) {
      setEmailError("Enter an email first");
      return;
    }
    const existing = existingSettings.find((s) => s.event === eventDef.event);

    if (nextSubscribed && !existing) {
      await commit({
        upsert: [
          {
            email: effectiveEmail,
            event: eventDef.event,
            type: SupplierNotificationSettingType.To,
          },
        ],
        delete: [],
      });
    } else if (!nextSubscribed && existing) {
      await commit({ upsert: [], delete: [existing.id] });
    }
  }

  async function changeType(
    eventDef: EventDef,
    nextType: SupplierNotificationSettingType,
  ) {
    const existing = existingSettings.find((s) => s.event === eventDef.event);
    if (!existing) return;
    await commit({
      upsert: [
        {
          id: existing.id,
          email: effectiveEmail,
          event: eventDef.event,
          type: nextType,
        },
      ],
      delete: [],
    });
  }

  const isEdit = !!email;

  return (
    <BaseDialog
      title={isEdit ? "Edit recipient" : "Add recipient"}
      subtitle={
        isEdit
          ? `Notifications for ${effectiveEmail}`
          : "Changes are saved automatically"
      }
    >
      <div className="col-span-full space-y-6">
        <Field>
          <Label>Email</Label>
          <Input
            type="email"
            autoFocus={!isEdit}
            value={emailDraft}
            invalid={!!emailError}
            onChange={(e) => {
              setEmailDraft(e.target.value);
              if (emailError) setEmailError(null);
            }}
            onBlur={handleEmailBlur}
            placeholder="recipient@example.com"
          />
          {emailError ? (
            <p className="mt-1 text-xs text-red-600">{emailError}</p>
          ) : null}
        </Field>

        <fieldset
          disabled={!isValidEmail}
          className="space-y-6 disabled:opacity-50"
        >
          {sections.map((section) => (
            <SubscriptionSection
              key={section.title}
              section={section}
              existingSettings={existingSettings}
              onToggle={toggleSubscribed}
              onChangeType={changeType}
            />
          ))}
        </fieldset>
      </div>
    </BaseDialog>
  );
}

type ExistingSetting = {
  id: string;
  event: SupplierNotificationSettingEvent;
  type: SupplierNotificationSettingType;
};

function SubscriptionSection({
  section,
  existingSettings,
  onToggle,
  onChangeType,
}: {
  section: EventSection;
  existingSettings: ExistingSetting[];
  onToggle: (eventDef: EventDef, next: boolean) => void;
  onChangeType: (
    eventDef: EventDef,
    next: SupplierNotificationSettingType,
  ) => void;
}) {
  return (
    <div>
      <h3 className="mb-3 text-sm font-medium text-gray-900">
        {section.title}
      </h3>
      <div className="divide-y divide-gray-100 overflow-hidden rounded-lg border border-gray-200 bg-white">
        {section.events.map((eventDef) => {
          const existing = existingSettings.find(
            (s) => s.event === eventDef.event,
          );
          const subscribed = !!existing;
          const type = existing?.type ?? SupplierNotificationSettingType.To;
          const Icon = eventDef.Icon;

          return (
            <Headless.Field
              key={eventDef.event}
              className="flex items-center gap-3 px-4 py-3"
            >
              <Checkbox
                checked={subscribed}
                onChange={(checked) => onToggle(eventDef, checked)}
              />
              <Label className="flex flex-1 cursor-pointer items-center gap-3">
                <Icon className="h-4 w-4 text-gray-400" />
                <span className="text-sm font-medium text-gray-900">
                  {eventDef.label}
                </span>
              </Label>
              <Select
                value={type}
                disabled={!subscribed}
                onChange={(e) =>
                  onChangeType(
                    eventDef,
                    e.target.value as SupplierNotificationSettingType,
                  )
                }
                className="w-24"
              >
                <option value={SupplierNotificationSettingType.To}>To</option>
                <option value={SupplierNotificationSettingType.Cc}>Cc</option>
                <option value={SupplierNotificationSettingType.Bcc}>Bcc</option>
              </Select>
            </Headless.Field>
          );
        })}
      </div>
    </div>
  );
}
